// The mini-games' sprites, cut from the Aseprite exports in client/art/aseprite/<piece>/ (built by art/aseprite/src/*.lua).
// Nothing is drawn here: a frame is a rectangle of an exported PNG, found by name in the sheet JSON Aseprite wrote beside it
// (its frames and tags), and copied 1:1 onto the frame's 180 x 300 canvas at whole pixels. No scaling, no rotation, no smoothing.
const BASE = new URL('./art/aseprite/', import.meta.url);
const sheets = new Map();
const waiting = new Set();
let pending = 0;

function loadImage(url) {
  return new Promise((ok, no) => { const img = new Image(); img.onload = () => ok(img); img.onerror = no; img.src = url; });
}

// A sheet: piece/name. Multi-frame pieces have <name>-sheet.png + -sheet.json; a still is <name>.png.
// Returns { ready, img, frames: [{x, y, w, h}], tags: { name: [from, to] } } at once; `ready` turns true when loaded.
export function sheet(piece, name, { still = false, data = null } = {}) {
  const key = piece + '/' + name;
  if (sheets.has(key)) return sheets.get(key);
  const s = { ready: false, img: null, frames: [], tags: {}, data: null };
  sheets.set(key, s);
  if (typeof Image === 'undefined' || typeof fetch === 'undefined') return s; // node (tests): the art is never loaded
  pending++;
  const dir = new URL(piece + '/', BASE);
  const jobs = still
    ? [loadImage(new URL(name + '.png', dir)).then(img => { s.img = img; s.frames = [{ x: 0, y: 0, w: img.naturalWidth, h: img.naturalHeight }]; })]
    : [fetch(new URL(name + '-sheet.json', dir)).then(r => r.json()).then(j => {
        s.frames = j.frames.map(f => f.frame);
        for (const t of j.meta.frameTags) s.tags[t.name] = [t.from, t.to];
        return loadImage(new URL(j.meta.image, dir)).then(img => { s.img = img; });
      })];
  if (data) jobs.push(fetch(new URL(data, dir)).then(r => r.json()).then(d => { s.data = d; }));
  Promise.all(jobs).then(() => { s.ready = true; }, () => {}).finally(() => {
    pending--;
    if (!pending) { const cbs = [...waiting]; waiting.clear(); for (const cb of cbs) cb(); }
  });
  return s;
}

// Call cb once every sheet asked for so far has loaded (or at once if none is loading).
export function whenArt(cb) { if (!pending) cb(); else waiting.add(cb); }

// The index of a frame: a tag's first frame plus k (clamped to the tag), or a plain index.
export function frameOf(s, tag, k = 0) {
  const t = s.tags[tag];
  if (!t) return typeof tag === 'number' ? tag : 0;
  return Math.min(t[1], t[0] + Math.max(0, k));
}

// Copy the part (sx, sy, w, h) of frame i of sheet s to (x, y): a tile cut to the edge of the area it fills. False until loaded.
export function blitPart(g, s, i, sx, sy, w, h, x, y) {
  if (!s.ready) return false;
  const f = s.frames[i] ?? s.frames[0];
  w = Math.min(w, f.w - sx); h = Math.min(h, f.h - sy);
  if (w <= 0 || h <= 0) return true;
  g.drawImage(s.img, f.x + sx, f.y + sy, w, h, Math.round(x), Math.round(y), w, h);
  return true;
}

// Copy frame i of sheet s with its top-left at (x, y); sw/sh crop it (a progress strip). False until the art has loaded.
export function blit(g, s, i, x, y, sw, sh) {
  if (!s.ready) return false;
  const f = s.frames[i] ?? s.frames[0];
  const w = sw ?? f.w, h = sh ?? f.h;
  if (w <= 0 || h <= 0) return true;
  g.drawImage(s.img, f.x, f.y, w, h, Math.round(x), Math.round(y), w, h);
  return true;
}
